import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  Input,
  Select,
  Switch,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi, ADDRESS_TYPES } from '../suppliersApi';

const EMPTY = {
  type: 'Headquarters', label: '', line1: '', line2: '', city: '', state: '', postalCode: '', country: '', isPrimary: false,
};

const normalize = (a) => (a
  ? {
    type: a.type ?? 'Headquarters', label: a.label ?? '', line1: a.line1 ?? '', line2: a.line2 ?? '',
    city: a.city ?? '', state: a.state ?? '', postalCode: a.postalCode ?? '', country: a.country ?? '', isPrimary: !!a.isPrimary,
  }
  : { ...EMPTY });

function AddressForm({ initial, submitting, onSubmit, onCancel }) {
  const t = useT();
  const [form, setForm] = useState(() => normalize(initial));
  useEffect(() => { setForm(normalize(initial)); }, [initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const typeOptions = useMemo(
    () => ADDRESS_TYPES.map((o) => ({ ...o, label: t(o.label) })),
    [t],
  );

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      type: form.type,
      label: form.label.trim() || null,
      line1: form.line1.trim() || null,
      line2: form.line2.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      postalCode: form.postalCode.trim() || null,
      country: form.country.trim() || null,
      isPrimary: form.isPrimary,
    });
  };

  return (
    <form className="sp-form" onSubmit={submit}>
      <div className="sp-form__row">
        <div className="ps-field">
          <span className="sp-field-label">{t('Type')}</span>
          <Select value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v }))} options={typeOptions} ariaLabel={t('Address type')} />
        </div>
        <Input label={t('Label')} value={form.label} onChange={set('label')} placeholder={t('Main warehouse')} />
      </div>
      <Input label={t('Address line 1')} value={form.line1} onChange={set('line1')} autoFocus />
      <Input label={t('Address line 2')} value={form.line2} onChange={set('line2')} />
      <div className="sp-form__row--3">
        <Input label={t('City')} value={form.city} onChange={set('city')} />
        <Input label={t('State / Province')} value={form.state} onChange={set('state')} />
        <Input label={t('Postal code')} value={form.postalCode} onChange={set('postalCode')} />
      </div>
      <Input label={t('Country')} value={form.country} onChange={set('country')} />
      <Switch checked={form.isPrimary} onChange={(v) => setForm((f) => ({ ...f, isPrimary: v }))} label={t('Primary address')} />
      <div className="sp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>{t('Cancel')}</Button>
        <Button type="submit" variant="primary" loading={submitting}>{t('Save address')}</Button>
      </div>
    </form>
  );
}

export default function AddressesTab({ supplier, onChanged }) {
  const toast = useToast();
  const t = useT();
  const addresses = supplier.addresses || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (a) => { setEditing(a); setDrawerOpen(true); };
  const close = () => { if (!submitting) { setDrawerOpen(false); setEditing(null); } };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const res = editing
        ? await suppliersApi.updateAddress(supplier.id, editing.id, payload)
        : await suppliersApi.addAddress(supplier.id, payload);
      onChanged(res);
      toast.success(editing ? t('Address updated') : t('Address added'));
      setDrawerOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(t('Could not save address'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await suppliersApi.removeAddress(supplier.id, deleteTarget.id);
      onChanged(res);
      toast.success(t('Address removed'));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(t('Could not remove address'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const lines = (a) => [a.line1, a.line2, [a.postalCode, a.city].filter(Boolean).join(' '), a.state, a.country]
    .filter(Boolean).join(', ');

  return (
    <div className="sp-panel">
      <div className="sp-section-head">
        <h2 className="sp-section-head__title">{t('Addresses')}</h2>
        <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add address')}</Button>
      </div>

      {addresses.length === 0 ? (
        <EmptyState
          icon={<Icon.MapPin size={28} />}
          title={t('No addresses yet')}
          description={t('Add billing, shipping and remittance addresses.')}
          action={<Button variant="primary" leadingIcon={<Icon.Plus size={15} />} onClick={openCreate}>{t('Add address')}</Button>}
        />
      ) : (
        <div className="sp-list">
          {addresses.map((a) => (
            <div key={a.id} className="sp-item">
              <div className="sp-item__main">
                <span className="sp-item__title">
                  <Icon.MapPin size={15} /> {a.label || t(a.type)}
                  <Badge tone="neutral">{t(a.type)}</Badge>
                  {a.isPrimary && <Badge tone="brand" dot>{t('Primary')}</Badge>}
                </span>
                <span className="sp-item__meta">{lines(a) || '—'}</span>
              </div>
              <div className="sp-item__actions">
                <IconButton aria-label={t('Edit address')} variant="ghost" size="icon-sm" onClick={() => openEdit(a)}>
                  <Icon.Pencil size={15} />
                </IconButton>
                <IconButton aria-label={t('Remove address')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(a)}>
                  <Icon.Trash size={15} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={drawerOpen} onClose={close} title={editing ? t('Edit address') : t('Add address')} size="md">
        <AddressForm initial={editing} submitting={submitting} onSubmit={handleSubmit} onCancel={close} />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Remove address')}
        description={deleteTarget ? t('Remove this {type} address?', { type: t(deleteTarget.type) }) : ''}
        confirmLabel={t('Remove')}
        tone="danger"
        loading={deleting}
      />
    </div>
  );
}
